import { useState, useEffect, useRef } from 'react';
import { Check, ChevronsUpDown, Search, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils'; // Assuming you have a utils file for classnames

interface Option {
    value: string;
    label: string;
    description?: string; // For extra info like "22 skids"
}

interface ComboboxProps {
    options: Option[];
    value?: string;
    onChange: (value: string) => void;
    onSearch?: (query: string) => void;
    placeholder?: string;
    loading?: boolean;
    disabled?: boolean;
    emptyText?: string;
    className?: string;
}

export function Combobox({
    options,
    value,
    onChange,
    onSearch,
    placeholder = "Select...",
    loading = false,
    disabled = false,
    emptyText = "No results found.",
    className
}: ComboboxProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Focus input when opened
    useEffect(() => {
        if (open && inputRef.current) {
            // Small timeout to ensure rendering
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    // Debounce search if onSearch provided
    useEffect(() => {
        if (onSearch) {
            const timer = setTimeout(() => {
                onSearch(query);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [query, onSearch]);

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Filter options client-side if NO onSearch is provided
    const filteredOptions = onSearch
        ? options
        : options.filter(opt => {
            const lowerQuery = query.toLowerCase();
            return (
                opt.label.toLowerCase().includes(lowerQuery) ||
                (opt.description && opt.description.toLowerCase().includes(lowerQuery))
            );
        });

    const selectedOption = options.find(opt => opt.value === value);

    return (
        <div ref={wrapperRef} className={cn("relative w-full", className)}>
            <div
                className={cn(
                    "flex h-10 w-full items-center justify-between rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 shadow-sm ring-offset-slate-950 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-50",
                    open ? "ring-2 ring-primary border-primary" : "",
                    disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                )}
                onClick={() => !disabled && setOpen(!open)}
            >
                <span className="truncate">
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </div>

            {open && (
                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-700 bg-slate-900 py-1 text-slate-50 shadow-md ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm animate-in fade-in-0 zoom-in-95">
                    <div className="flex items-center border-b border-slate-700 px-3 sticky top-0 bg-slate-900 z-10">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <input
                            ref={inputRef}
                            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder="Search..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        {loading && <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />}
                    </div>
                    <div className="p-1">
                        {filteredOptions.length === 0 && !loading ? (
                            <div className="py-6 text-center text-sm text-slate-500">
                                {emptyText}
                            </div>
                        ) : (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    className={cn(
                                        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-slate-800 hover:text-slate-50 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                                        value === option.value ? "bg-slate-800" : ""
                                    )}
                                    onClick={() => {
                                        onChange(option.value);
                                        setOpen(false);
                                        setQuery(""); // Reset query ONLY if you want to clear search on select
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            value === option.value ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    <div className="flex flex-col">
                                        <span>{option.label}</span>
                                        {option.description && (
                                            <span className="text-xs text-slate-500">{option.description}</span>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
